import {
  signalingTranscriptionSpokenLanguageChanged,
  signalingTranscriptionStarted,
  signalingTranscriptionStopped,
  speechCaptioned,
} from 'features/transcripts/actions';
import {
  signalingBulkContentClosed,
  signalingContentPushed,
  signalingContentUnlocked,
  signalingWhiteboardAlreadyOpened,
  signalingWhiteboardChanged,
  signalingWhiteboardClosed,
  signalingWhiteboardCreated,
  signalingWhiteboardCursorUpdated,
  signalingWhiteboardOpened,
} from 'features/layout/features/content/actions';
import {
  activeSpeakerChanged,
  audioMutedRemotely,
  audioUnmuteAsked,
  externalStreamStarted,
  externalStreamStopped,
  signalingAspectRatioAdjusted,
  speakerStoppedTalking,
  streamStarted,
  streamStopped,
  videoMutedRemotely,
  videoUnmuteAsked,
} from 'features/streaming/actions';
import {
  screenshareStarted,
  screenshareStopped,
  screenshareStoppedRemotely,
} from 'features/screenshare/actions';
import { ActionCreator } from '@reduxjs/toolkit';
import { SignalingReceivedEventType } from 'services/signaling/types';
import {
  pong,
  signalingConnected,
  signalingContentCleared,
  signalingDisconnected,
  systemUpdate,
} from 'features/application/actions';
import {
  participantsLimitReached,
  sessionEnded,
  sessionLeft,
  signalingBroadcasterLimitReached,
  signalingEntryAccessAwaitingStarted,
  signalingLobbyMessageReceived,
  signalingRoomJoined,
  signalingRoomJoinPaused,
  signalingRoomLocked,
  signalingRoomUnlocked,
  signalingScreenshareLimitReached,
  signalingSessionAwaitingStarted,
  signalingSessionEndsSoon,
} from 'features/room/actions';
import {
  signalingAccessRejected,
  signalingAvatarChanged,
  signalingAvatarDeleted,
  signalingBulkAccessRejected,
  signalingHandLowered,
  signalingHandRaised,
  signalingUserBanned,
  signalingUserJoined,
  signalingUserKicked,
  signalingUserLeft,
  signalingUserLeftBatch,
  signalingUserRenamed,
  signalingWaitingUserJoined,
  signalingWaitingUserLeft,
  signalingWaitingUserLeftBatch,
  signalingWaitingUsersUpdated,
} from 'features/users/actions';
import {
  signalingRecorderJoined,
  signalingRecordingBookmarkCreated,
  signalingRecordingFailed,
  signalingRecordingRequested,
  signalingRecordingStarted,
  signalingRecordingStopped,
  signalingRecordingStopRequested,
} from 'features/recorder/actions';
import {
  signalingRestreamerJoined,
  signalingRestreamingFailure,
  signalingRestreamingRequested,
  signalingRestreamingStarted,
  signalingRestreamingStopped,
  signalingRestreamingStopRequested,
} from 'features/restreamer/actions';
import {
  signalingAllChatMessagesReceived,
  signalingChatImageDeleted,
  signalingChatMessageDeleted,
  signalingChatMessageSent,
  signalingChatMessageUploadPrepared,
  signalingChatReactionToggled,
  signalingPublicChatCleared,
} from 'features/chat/actions';
import {
  signalingBroadcastAllowed,
  signalingBroadcastDisallowed,
  signalingEditWhiteboardAllowed,
  signalingEditWhiteboardDisallowed,
  signalingPresentContentAllowed,
  signalingPresentContentDisallowed,
  signalingRoleChanged,
  signalingScreenshareAllowed,
  signalingScreenshareDisallowed,
} from 'features/permissions/actions';
import {
  signalingEncryptedNameShared,
  signalingEncryptionKeyShared,
  signalingPublicKeyShared,
  signalingWhiteboardKeyRequested,
  signalingWhiteboardKeyShared,
} from 'features/e2ee/actions';
import {
  signalingBreakoutRoomAdded,
  signalingBreakoutRoomAssistanceRequested,
  signalingBreakoutRoomDeleted,
  signalingBreakoutRoomMessageBroadcast,
  signalingBreakoutRoomRenamed,
  signalingBreakoutRoomsCleared,
  signalingBreakoutRoomsCreated,
  signalingBreakoutRoomsEdited,
  signalingBreakoutRoomsReceived,
  signalingBreakoutRoomsStarted,
  signalingBreakoutRoomsStopped,
  signalingBreakoutUserJoined,
  signalingBreakoutUserLeft,
  signalingBreakoutUserLeftBatch,
  signalingUsersAssignedToBreakoutRoom,
  signalingUserUnassignedFromBreakoutRoom,
} from 'features/breakout-rooms/actions';
import {
  fileUploadPrepared,
  signalingPrivateOpenedWhiteboardsReceived,
  signalingWhiteboardDataReceived,
  signalingWhiteboardDataRequestRejected,
  signalingWhiteboardModeChanged,
} from 'features/whiteboard/actions';
import {
  signalingAnswerDeleted,
  signalingAnswerEdited,
  signalingQuestionAnswered,
  signalingQuestionAsked,
  signalingQuestionDeleted,
  signalingQuestionDismissed,
  signalingQuestionEdited,
  signalingQuestionLiveAnswerCancelled,
  signalingQuestionLiveAnswerStarted,
  signalingQuestionLiveAnswerStopped,
  signalingQuestionReopened,
  signalingQuestionsReceived,
  signalingQuestionUpvoted,
  signalingQuestionUpvoteRemoved,
} from 'features/qa/actions';
import { signalingPinnedPanelsChanged } from 'features/side-panels/sidePanelsSlice';
import { signalingFeatureDisabled, signalingFeatureEnabled } from 'features/feature-set/actions';
import {
  signalingPollCreated,
  signalingPollDeleted,
  signalingPollEdited,
  signalingPollEnded,
  signalingPollLaunched,
  signalingPollResultsShared,
  signalingPollResultsSharingStopped,
  signalingPollsReceived,
  signalingPollTemplateCreated,
  signalingPollTemplateDeleted,
  signalingPollTemplateEdited,
  signalingPollTemplatesReceived,
  signalingPollVoted,
} from 'features/polls/actions';
import {
  signalingQuizCreated,
  signalingQuizDeleted,
  signalingQuizEdited,
  signalingQuizEnded,
  signalingQuizFinished,
  signalingQuizLaunched,
  signalingQuizTemplateCreated,
  signalingQuizTemplateDeleted,
  signalingQuizTemplateEdited,
  signalingQuizTemplatesReceived,
  signalingQuizzesReceived,
} from 'features/quizzes/actions';
import {
  signalingContentLibraryConversionFailed,
  signalingContentLibraryConversionProgress,
  signalingContentLibraryEmbedContentCreated,
  signalingContentLibraryFileClosed,
  signalingContentLibraryFileConverted,
  signalingContentLibraryFileCopied,
  signalingContentLibraryFileDeleted,
  signalingContentLibraryFileMoved,
  signalingContentLibraryFileOpened,
  signalingContentLibraryFilePageChanged,
  signalingContentLibraryFileRenamed,
  signalingContentLibraryFileShared,
  signalingContentLibraryFileUnshared,
  signalingContentLibraryFileUploadAccepted,
  signalingContentLibraryFileUploaded,
  signalingContentLibraryFolderCreated,
  signalingContentLibraryFolderDeleted,
  signalingContentLibraryFolderMoved,
  signalingContentLibraryFolderRenamed,
  signalingContentLibraryMediaPlayerStateChanged,
  signalingContentLibraryOpenedFilesReceived,
  signalingContentLibrarySharedFilesReceived,
  signalingLibraryReceived,
} from 'features/content-library/actions';
import {
  signalingVirtualBackgroundDeleted,
  signalingVirtualBackgroundUploadAccepted,
  signalingVirtualBackgroundUploaded,
  signalingVirtualBackgroundUploadRejected,
} from 'features/virtual-backgrounds/actions';
import {
  signalingPhoneBridgeConnected,
  signalingPhoneBridgeConnecting,
  signalingPhoneBridgeDisconnected,
  signalingPhoneStreamStarted,
  signalingPhoneUserAskedToUnmute,
  signalingPhoneUserHandLowered,
  signalingPhoneUserHandRaised,
  signalingPhoneUserMuted,
  signalingPhoneUsersJoined,
  signalingPhoneUsersLeft,
  signalingPhoneUserUnmuted,
} from 'features/sip/actions';
import { signalingSharedNotesReceived } from 'features/shared-notes/actions';
import { signalingRoomReactionsReceived } from 'features/room-reactions/actions';
import { signalingLockedContentZoomUpdated } from 'features/content-zoom/actions';
import {
  signalingContentWhiteboardDataReceived,
  signalingContentWhiteboardLaserUpdated,
  signalingContentWhiteboardUpdated,
  signalingScreenshareWhiteboardDataReceived,
  signalingScreenshareWhiteboardLaserUpdated,
  signalingScreenshareWhiteboardUpdated,
} from 'features/content-whiteboard/actions';

export const eventMap: Record<SignalingReceivedEventType, ActionCreator<any>> = {
  pong,
  streamStarted,
  streamStopped,
  screenshareStarted,
  screenshareStopped,
  screenshareStoppedRemotely,
  externalStreamStarted,
  externalStreamStopped,
  activeSpeakerChanged,
  speakerStoppedTalking,
  sessionEnded,
  sessionLeft,
  participantsLimitReached,
  userLeft: signalingUserLeft,
  userJoined: signalingUserJoined,
  connected: signalingConnected,
  roomJoined: signalingRoomJoined,
  disconnect: signalingDisconnected,
  audioMutedRemotely,
  askedToUnmuteAudioRemotely: audioUnmuteAsked,
  videoMutedRemotely,
  askedToUnmuteVideoRemotely: videoUnmuteAsked,
  recordingStarted: signalingRecordingStarted,
  recordingRequested: signalingRecordingRequested,
  recordingStopRequested: signalingRecordingStopRequested,
  recordingFailure: signalingRecordingFailed,
  recordingStopped: signalingRecordingStopped,
  recorderJoined: signalingRecorderJoined,
  restreamingStarted: signalingRestreamingStarted,
  restreamingRequested: signalingRestreamingRequested,
  restreamingStopRequested: signalingRestreamingStopRequested,
  restreamingFailure: signalingRestreamingFailure,
  restreamingStopped: signalingRestreamingStopped,
  restreamerJoined: signalingRestreamerJoined,
  chatMessageSent: signalingChatMessageSent,
  // No reducers/sagas listen to this action — the chat image upload saga reads
  // the response directly via SignalingSocket.sendAsync. Kept here to preserve
  // the uniform eventMap pattern; dispatching it is a side-effect-free no-op.
  chatMessageUploadPrepared: signalingChatMessageUploadPrepared,
  publicChatCleared: signalingPublicChatCleared,
  chatMessageDeleted: signalingChatMessageDeleted,
  chatImageDeleted: signalingChatImageDeleted,
  allChatMessagesReceived: signalingAllChatMessagesReceived,
  chatReactionToggled: signalingChatReactionToggled,
  transcriptionStarted: signalingTranscriptionStarted,
  transcriptionStopped: signalingTranscriptionStopped,
  speechCaptioned,
  handRaised: signalingHandRaised,
  handLowered: signalingHandLowered,
  kicked: signalingUserKicked,
  broadcastAllowed: signalingBroadcastAllowed,
  broadcastDisallowed: signalingBroadcastDisallowed,
  screenshareAllowed: signalingScreenshareAllowed,
  screenshareDisallowed: signalingScreenshareDisallowed,
  roleChanged: signalingRoleChanged,
  systemUpdate,
  sessionNotStarted: signalingSessionAwaitingStarted,
  encryptionKeyShared: signalingEncryptionKeyShared,
  entryAccessRequired: signalingEntryAccessAwaitingStarted,
  roomLocked: signalingRoomLocked,
  roomUnlocked: signalingRoomUnlocked,
  waitingUserJoined: signalingWaitingUserJoined,
  waitingUserLeft: signalingWaitingUserLeft,
  waitingUserLeftBatch: signalingWaitingUserLeftBatch,
  waitingUsersUpdated: signalingWaitingUsersUpdated,
  bulkUserAccessRejected: signalingBulkAccessRejected,
  accessRejected: signalingAccessRejected,
  publicKeyShared: signalingPublicKeyShared,
  encryptedNameShared: signalingEncryptedNameShared,
  whiteboardKeyShared: signalingWhiteboardKeyShared,
  whiteboardKeyRequested: signalingWhiteboardKeyRequested,
  whiteboardOpened: signalingWhiteboardOpened,
  whiteboardClosed: signalingWhiteboardClosed,
  bulkContentClosed: signalingBulkContentClosed,
  whiteboardCreated: signalingWhiteboardCreated,
  whiteboardUpdated: signalingWhiteboardChanged,
  whiteboardModeChanged: signalingWhiteboardModeChanged,
  privateOpenedWhiteboardsReceived: signalingPrivateOpenedWhiteboardsReceived,
  whiteboardCursorUpdated: signalingWhiteboardCursorUpdated,
  editWhiteboardAllowed: signalingEditWhiteboardAllowed,
  editWhiteboardDisallowed: signalingEditWhiteboardDisallowed,
  presentContentAllowed: signalingPresentContentAllowed,
  presentContentDisallowed: signalingPresentContentDisallowed,
  whiteboardAlreadyOpened: signalingWhiteboardAlreadyOpened,
  whiteboardDataReceived: signalingWhiteboardDataReceived,
  whiteboardDataRequestRejected: signalingWhiteboardDataRequestRejected,
  contentWhiteboardUpdated: signalingContentWhiteboardUpdated,
  contentWhiteboardDataReceived: signalingContentWhiteboardDataReceived,
  contentWhiteboardLaserUpdated: signalingContentWhiteboardLaserUpdated,
  screenshareWhiteboardUpdated: signalingScreenshareWhiteboardUpdated,
  screenshareWhiteboardDataReceived: signalingScreenshareWhiteboardDataReceived,
  screenshareWhiteboardLaserUpdated: signalingScreenshareWhiteboardLaserUpdated,
  roomJoinPaused: signalingRoomJoinPaused,
  broadcasterLimitReached: signalingBroadcasterLimitReached,
  screenshareLimitReached: signalingScreenshareLimitReached,
  fileUploadPrepared,
  breakoutRoomsCreated: signalingBreakoutRoomsCreated,
  breakoutRoomsEdited: signalingBreakoutRoomsEdited,
  breakoutRoomAdded: signalingBreakoutRoomAdded,
  breakoutRoomsReceived: signalingBreakoutRoomsReceived,
  breakoutRoomsCleared: signalingBreakoutRoomsCleared,
  breakoutRoomDeleted: signalingBreakoutRoomDeleted,
  breakoutRoomRenamed: signalingBreakoutRoomRenamed,
  breakoutRoomsStarted: signalingBreakoutRoomsStarted,
  breakoutRoomsStopped: signalingBreakoutRoomsStopped,
  usersAssignedToBreakoutRoom: signalingUsersAssignedToBreakoutRoom,
  userUnassignedFromBreakoutRoom: signalingUserUnassignedFromBreakoutRoom,
  breakoutRoomMessageBroadcasted: signalingBreakoutRoomMessageBroadcast,
  breakoutUserJoined: signalingBreakoutUserJoined,
  breakoutUserLeft: signalingBreakoutUserLeft,
  breakoutUserLeftBatch: signalingBreakoutUserLeftBatch,
  questionEdited: signalingQuestionEdited,
  questionAsked: signalingQuestionAsked,
  questionAnswered: signalingQuestionAnswered,
  questionDeleted: signalingQuestionDeleted,
  answerDeleted: signalingAnswerDeleted,
  answerEdited: signalingAnswerEdited,
  questionLiveAnswerStarted: signalingQuestionLiveAnswerStarted,
  questionLiveAnswerStopped: signalingQuestionLiveAnswerStopped,
  questionLiveAnswerCancelled: signalingQuestionLiveAnswerCancelled,
  questionDismissed: signalingQuestionDismissed,
  questionReopened: signalingQuestionReopened,
  questionsReceived: signalingQuestionsReceived,
  featureEnabled: signalingFeatureEnabled,
  featureDisabled: signalingFeatureDisabled,
  pinnedPanelsChanged: signalingPinnedPanelsChanged,
  pollCreated: signalingPollCreated,
  pollEnded: signalingPollEnded,
  pollLaunched: signalingPollLaunched,
  pollsReceived: signalingPollsReceived,
  pollDeleted: signalingPollDeleted,
  pollVoted: signalingPollVoted,
  pollResultsShared: signalingPollResultsShared,
  pollResultsSharingStopped: signalingPollResultsSharingStopped,
  pollEdited: signalingPollEdited,
  pollTemplatesReceived: signalingPollTemplatesReceived,
  pollTemplateCreated: signalingPollTemplateCreated,
  pollTemplateEdited: signalingPollTemplateEdited,
  pollTemplateDeleted: signalingPollTemplateDeleted,
  quizCreated: signalingQuizCreated,
  quizLaunched: signalingQuizLaunched,
  quizEnded: signalingQuizEnded,
  quizDeleted: signalingQuizDeleted,
  quizEdited: signalingQuizEdited,
  quizzesReceived: signalingQuizzesReceived,
  quizFinished: signalingQuizFinished,
  quizTemplatesReceived: signalingQuizTemplatesReceived,
  quizTemplateCreated: signalingQuizTemplateCreated,
  quizTemplateEdited: signalingQuizTemplateEdited,
  quizTemplateDeleted: signalingQuizTemplateDeleted,
  aspectRatioAdjusted: signalingAspectRatioAdjusted,
  questionUpvoted: signalingQuestionUpvoted,
  questionUpvoteRemoved: signalingQuestionUpvoteRemoved,
  sessionEndsSoon: signalingSessionEndsSoon,
  userLeftBatch: signalingUserLeftBatch,
  libraryReceived: signalingLibraryReceived,
  libraryFolderCreated: signalingContentLibraryFolderCreated,
  libraryFolderRenamed: signalingContentLibraryFolderRenamed,
  libraryFileRenamed: signalingContentLibraryFileRenamed,
  libraryFileMoved: signalingContentLibraryFileMoved,
  libraryFolderMoved: signalingContentLibraryFolderMoved,
  libraryFileDeleted: signalingContentLibraryFileDeleted,
  libraryFolderDeleted: signalingContentLibraryFolderDeleted,
  libraryFileUploaded: signalingContentLibraryFileUploaded,
  libraryFileUploadAccepted: signalingContentLibraryFileUploadAccepted,
  virtualBackgroundUploadAccepted: signalingVirtualBackgroundUploadAccepted,
  virtualBackgroundUploadRejected: signalingVirtualBackgroundUploadRejected,
  virtualBackgroundUploaded: signalingVirtualBackgroundUploaded,
  virtualBackgroundDeleted: signalingVirtualBackgroundDeleted,
  libraryFileOpened: signalingContentLibraryFileOpened,
  libraryFileClosed: signalingContentLibraryFileClosed,
  libraryFileConversionFailed: signalingContentLibraryConversionFailed,
  libraryFileConversionProgress: signalingContentLibraryConversionProgress,
  libraryFileConverted: signalingContentLibraryFileConverted,
  libraryFilePageChanged: signalingContentLibraryFilePageChanged,
  libraryWebappCreated: signalingContentLibraryEmbedContentCreated,
  mediaPlayerStateChanged: signalingContentLibraryMediaPlayerStateChanged,
  phoneBridgeConnected: signalingPhoneBridgeConnected,
  phoneBridgeConnecting: signalingPhoneBridgeConnecting,
  phoneBridgeDisconnected: signalingPhoneBridgeDisconnected,
  phoneStreamStarted: signalingPhoneStreamStarted,
  libraryOpenedFilesReceived: signalingContentLibraryOpenedFilesReceived,
  contentPushed: signalingContentPushed,
  contentUnlocked: signalingContentUnlocked,
  lockedContentZoomUpdated: signalingLockedContentZoomUpdated,
  phoneUserMuted: signalingPhoneUserMuted,
  phoneUserUnmuted: signalingPhoneUserUnmuted,
  phoneUsersJoined: signalingPhoneUsersJoined,
  phoneUsersLeft: signalingPhoneUsersLeft,
  phoneUserHandLowered: signalingPhoneUserHandLowered,
  phoneUserHandRaised: signalingPhoneUserHandRaised,
  phoneUserAskedToUnmute: signalingPhoneUserAskedToUnmute,
  sharedNotesReceived: signalingSharedNotesReceived,
  banned: signalingUserBanned,
  lobbyMessageReceived: signalingLobbyMessageReceived,
  libraryFileShared: signalingContentLibraryFileShared,
  libraryFileUnshared: signalingContentLibraryFileUnshared,
  sharedLibraryFilesReceived: signalingContentLibrarySharedFilesReceived,
  contentCleared: signalingContentCleared,
  libraryFileCopied: signalingContentLibraryFileCopied,
  breakoutModeratorRequested: signalingBreakoutRoomAssistanceRequested,
  recordingBookmarkCreated: signalingRecordingBookmarkCreated,
  userRenamed: signalingUserRenamed,
  roomReactionsReceived: signalingRoomReactionsReceived,
  spokenLanguageChanged: signalingTranscriptionSpokenLanguageChanged,
  avatarChanged: signalingAvatarChanged,
  avatarDeleted: signalingAvatarDeleted,
};
